import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  ChatCircle,
  User,
  PaperPlaneRight,
  ArrowClockwise,
  Circle,
  XCircle,
  Trash,
  ArrowCounterClockwise,
  Check,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function AdminChatSection() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: 'close' | 'delete', userId, userName }
  const scrollRef = useRef(null);

  const selectedThread = threads.find((t) => t.id === selected);
  const isClosed = !!selectedThread?.closed;

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/admin/chat/threads");
      setThreads(data.threads || []);
    } catch {}
  };

  const loadMessages = async (userId) => {
    setSelected(userId);
    try {
      const { data } = await api.get(`/admin/chat/thread/${userId}`);
      setMsgs(data.messages || []);
    } catch {}
  };

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 12000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (selected) {
      const t = setInterval(() => loadMessages(selected), 8000);
      return () => clearInterval(t);
    }
  }, [selected]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const reply = async () => {
    if (!selected || !text.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/chat/reply", { user_id: selected, message: text.trim() });
      setText("");
      await loadMessages(selected);
      await loadThreads();
    } catch {
      toast.error("Erro ao responder");
    } finally {
      setLoading(false);
    }
  };

  const closeThread = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await api.post(`/admin/chat/close/${selected}`);
      toast.success("Conversa encerrada");
      await loadMessages(selected);
      await loadThreads();
    } catch {
      toast.error("Erro ao encerrar");
    } finally {
      setLoading(false);
      setConfirm(null);
    }
  };

  const reopenThread = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await api.post(`/admin/chat/reopen/${selected}`);
      toast.success("Conversa reaberta");
      await loadThreads();
    } catch {
      toast.error("Erro ao reabrir");
    } finally {
      setLoading(false);
    }
  };

  const deleteThread = async () => {
    const userId = confirm?.userId;
    if (!userId) return;
    setLoading(true);
    try {
      await api.delete(`/admin/chat/thread/${userId}`);
      toast.success("Conversa apagada");
      if (selected === userId) {
        setSelected(null);
        setMsgs([]);
      }
      await loadThreads();
    } catch {
      toast.error("Erro ao apagar");
    } finally {
      setLoading(false);
      setConfirm(null);
    }
  };

  return (
    <div className="card-flat p-6 fadeUp" data-testid="admin-chat-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-caps flex items-center gap-1">
            <ChatCircle size={12} /> Suporte ao vivo
          </div>
          <h3 className="font-display font-black text-2xl tracking-tighter mt-1">
            Chat de Clientes
          </h3>
        </div>
        <button
          onClick={loadThreads}
          className="btn-ghost text-xs flex items-center gap-1"
          data-testid="admin-chat-refresh"
        >
          <ArrowClockwise size={12} /> Atualizar
        </button>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-4 h-[500px]">
        {/* Lista de threads */}
        <div className="border border-[var(--border)] rounded-sm overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-6 text-center text-white/50 text-xs">
              Nenhuma conversa ainda
            </div>
          ) : (
            threads.map((t) => (
              <div
                key={t.id}
                className={`group border-b border-white/5 relative ${
                  selected === t.id ? "bg-white/5 border-l-2 border-l-[var(--accent)]" : ""
                }`}
              >
                <button
                  onClick={() => loadMessages(t.id)}
                  data-testid={`thread-${t.id}`}
                  className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition"
                >
                  <div className="flex items-center gap-2">
                    <User size={14} className="shrink-0 text-white/40" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold truncate flex items-center gap-1.5">
                        {t.user_name}
                        {t.closed && (
                          <span
                            className="text-[8px] px-1 py-0.5 rounded-sm bg-white/10 text-white/60 font-mono uppercase tracking-wider"
                            data-testid={`thread-closed-badge-${t.id}`}
                          >
                            Encerrada
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-white/40 truncate">{t.user_email}</div>
                    </div>
                    {t.has_unread_admin && !t.closed && (
                      <Circle size={8} weight="fill" color="var(--accent)" />
                    )}
                  </div>
                  {t.last_message && (
                    <div className="text-[10px] text-white/50 mt-1 truncate pr-6">
                      {t.last_message}
                    </div>
                  )}
                </button>
                {/* Botão apagar (por thread, aparece no hover) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirm({ type: "delete", userId: t.id, userName: t.user_name });
                  }}
                  data-testid={`thread-delete-${t.id}`}
                  className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition p-1.5 rounded-sm hover:bg-[var(--danger)]/20 text-white/40 hover:text-[var(--danger)]"
                  title="Apagar conversa"
                >
                  <Trash size={13} weight="bold" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Painel de mensagens */}
        <div className="border border-[var(--border)] rounded-sm flex flex-col overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
              Selecione uma conversa
            </div>
          ) : (
            <>
              {/* Header do painel com ações */}
              <div className="px-3 py-2 border-b border-[var(--border)] bg-white/[0.02] flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="text-xs font-bold truncate">
                    {selectedThread?.user_name}
                  </div>
                  {isClosed && (
                    <span className="text-[8px] px-1.5 py-0.5 rounded-sm bg-white/10 text-white/60 font-mono uppercase tracking-wider shrink-0">
                      Encerrada
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isClosed ? (
                    <button
                      onClick={reopenThread}
                      disabled={loading}
                      data-testid="admin-chat-reopen"
                      className="text-[10px] px-2 py-1 rounded-sm border border-white/10 hover:border-[var(--accent)] hover:text-[var(--accent)] flex items-center gap-1 transition"
                      title="Reabrir conversa"
                    >
                      <ArrowCounterClockwise size={11} weight="bold" /> Reabrir
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        setConfirm({
                          type: "close",
                          userId: selected,
                          userName: selectedThread?.user_name,
                        })
                      }
                      disabled={loading}
                      data-testid="admin-chat-close"
                      className="text-[10px] px-2 py-1 rounded-sm border border-white/10 hover:border-[var(--warning,#eab308)] hover:text-[var(--warning,#eab308)] flex items-center gap-1 transition"
                      title="Encerrar conversa"
                    >
                      <XCircle size={11} weight="bold" /> Encerrar
                    </button>
                  )}
                  <button
                    onClick={() =>
                      setConfirm({
                        type: "delete",
                        userId: selected,
                        userName: selectedThread?.user_name,
                      })
                    }
                    disabled={loading}
                    data-testid="admin-chat-delete"
                    className="text-[10px] px-2 py-1 rounded-sm border border-white/10 hover:border-[var(--danger)] hover:text-[var(--danger)] flex items-center gap-1 transition"
                    title="Apagar conversa"
                  >
                    <Trash size={11} weight="bold" /> Apagar
                  </button>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
                {msgs.map((m) => {
                  if (m.sender_role === "system") {
                    return (
                      <div key={m.id} className="flex justify-center" data-testid={`sys-msg-${m.id}`}>
                        <div className="text-[10px] text-white/40 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                          {m.message}
                        </div>
                      </div>
                    );
                  }
                  const mine = m.sender_role === "admin";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                          mine
                            ? "bg-[var(--accent)] text-black"
                            : "bg-white/5 border border-white/10"
                        }`}
                      >
                        <div className="whitespace-pre-wrap break-words">{m.message}</div>
                        <div
                          className={`text-[9px] mt-1 ${
                            mine ? "text-black/50" : "text-white/40"
                          }`}
                        >
                          {mine ? "Você" : m.sender_name} ·{" "}
                          {new Date(m.created_at).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-2 border-t border-[var(--border)] flex gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !e.shiftKey && (e.preventDefault(), reply())
                  }
                  placeholder={
                    isClosed ? "Conversa encerrada — reabra para responder" : "Digite sua resposta..."
                  }
                  disabled={isClosed}
                  data-testid="admin-reply-input"
                  className="flex-1 bg-black/40 border border-[var(--border)] rounded-sm px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed"
                />
                <button
                  onClick={reply}
                  disabled={loading || !text.trim() || isClosed}
                  data-testid="admin-reply-send"
                  className="w-9 h-9 rounded-sm bg-[var(--accent)] text-black flex items-center justify-center disabled:opacity-40"
                >
                  <PaperPlaneRight size={13} weight="fill" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de confirmação */}
      {confirm && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !loading && setConfirm(null)}
          data-testid="chat-confirm-modal"
        >
          <div
            className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-6 max-w-sm w-full fadeUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              {confirm.type === "delete" ? (
                <div className="w-10 h-10 rounded-full bg-[var(--danger)]/20 flex items-center justify-center">
                  <Trash size={18} weight="bold" color="var(--danger)" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-[var(--warning,#eab308)]/20 flex items-center justify-center">
                  <XCircle size={18} weight="bold" color="var(--warning,#eab308)" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-display font-bold text-base">
                  {confirm.type === "delete" ? "Apagar conversa?" : "Encerrar conversa?"}
                </div>
                <div className="text-xs text-white/50 truncate">{confirm.userName}</div>
              </div>
            </div>
            <p className="text-sm text-white/70 mb-5">
              {confirm.type === "delete"
                ? "Todas as mensagens dessa conversa serão apagadas permanentemente. Essa ação não pode ser desfeita."
                : "O cliente verá uma mensagem informando que o atendimento foi encerrado. Você pode reabrir depois se precisar."}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirm(null)}
                disabled={loading}
                data-testid="chat-confirm-cancel"
                className="text-xs px-3 py-2 rounded-sm border border-white/10 hover:bg-white/5"
              >
                Cancelar
              </button>
              <button
                onClick={confirm.type === "delete" ? deleteThread : closeThread}
                disabled={loading}
                data-testid="chat-confirm-ok"
                className={`text-xs px-3 py-2 rounded-sm flex items-center gap-1.5 font-bold ${
                  confirm.type === "delete"
                    ? "bg-[var(--danger)] text-white hover:opacity-90"
                    : "bg-[var(--warning,#eab308)] text-black hover:opacity-90"
                } disabled:opacity-40`}
              >
                <Check size={12} weight="bold" />
                {confirm.type === "delete" ? "Apagar" : "Encerrar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
